import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Globe, MapPin, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

// Component to handle flag display with better cross-browser support
const FlagEmoji = ({ flag, className = "", countryCode }: { flag: string; className?: string; countryCode?: string }) => {
  const [useImage, setUseImage] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  // Try to use image flag first, fallback to emoji
  useEffect(() => {
    if (countryCode && countryCode !== 'XX' && !imageError) {
      setImageLoading(true);
      // Test if the flag image loads
      const img = new Image();
      img.onload = () => {
        setUseImage(true);
        setImageLoading(false);
      };
      img.onerror = () => {
        setImageError(true);
        setImageLoading(false);
      };
      img.src = `https://flagcdn.com/16x12/${countryCode.toLowerCase()}.png`;
    }
  }, [countryCode, imageError]);

  // For "Other" or invalid country codes, always use emoji
  if (!countryCode || countryCode === 'XX' || imageError) {
    return (
      <span 
        className={cn("emoji-flag", className)}
        style={{
          fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Twemoji", "EmojiOne Color", "Android Emoji", sans-serif',
          fontSize: 'inherit',
          display: 'inline-block',
          lineHeight: '1',
          verticalAlign: 'middle'
        }}
      >
        {flag}
      </span>
    );
  }

  if (useImage && countryCode && !imageError) {
    return (
      <span 
        className={cn("flag-image", className)}
        style={{
          display: 'inline-block',
          width: '1.2em',
          height: '0.8em',
          background: `url('https://flagcdn.com/16x12/${countryCode.toLowerCase()}.png') no-repeat center`,
          backgroundSize: 'contain',
          verticalAlign: 'middle'
        }}
      />
    );
  }

  // Show emoji while loading or as fallback
  return (
    <span 
      className={cn("emoji-flag", className)}
      style={{
        fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", "Twemoji", "EmojiOne Color", "Android Emoji", sans-serif',
        fontSize: 'inherit',
        display: 'inline-block',
        lineHeight: '1',
        verticalAlign: 'middle',
        opacity: imageLoading ? 0.7 : 1
      }}
    >
      {flag}
    </span>
  );
};

// Top countries list for better UX
const COUNTRIES = [
  { name: "United States", code: "US", flag: "🇺🇸" },
  { name: "United Kingdom", code: "GB", flag: "🇬🇧" },
  { name: "Canada", code: "CA", flag: "🇨🇦" },
  { name: "Australia", code: "AU", flag: "🇦🇺" },
  { name: "Germany", code: "DE", flag: "🇩🇪" },
  { name: "France", code: "FR", flag: "🇫🇷" },
  { name: "Spain", code: "ES", flag: "🇪🇸" },
  { name: "Italy", code: "IT", flag: "🇮🇹" },
  { name: "Netherlands", code: "NL", flag: "🇳🇱" },
  { name: "Sweden", code: "SE", flag: "🇸🇪" },
  { name: "Norway", code: "NO", flag: "🇳🇴" },
  { name: "Denmark", code: "DK", flag: "🇩🇰" },
  { name: "Finland", code: "FI", flag: "🇫🇮" },
  { name: "Switzerland", code: "CH", flag: "🇨🇭" },
  { name: "Austria", code: "AT", flag: "🇦🇹" },
  { name: "Belgium", code: "BE", flag: "🇧🇪" },
  { name: "Ireland", code: "IE", flag: "🇮🇪" },
  { name: "New Zealand", code: "NZ", flag: "🇳🇿" },
  { name: "Japan", code: "JP", flag: "🇯🇵" },
  { name: "South Korea", code: "KR", flag: "🇰🇷" },
  { name: "Singapore", code: "SG", flag: "🇸🇬" },
  { name: "India", code: "IN", flag: "🇮🇳" },
  { name: "Brazil", code: "BR", flag: "🇧🇷" },
  { name: "Mexico", code: "MX", flag: "🇲🇽" },
  { name: "Argentina", code: "AR", flag: "🇦🇷" },
  { name: "Chile", code: "CL", flag: "🇨🇱" },
  { name: "Colombia", code: "CO", flag: "🇨🇴" },
  { name: "Peru", code: "PE", flag: "🇵🇪" },
  { name: "South Africa", code: "ZA", flag: "🇿🇦" },
  { name: "Nigeria", code: "NG", flag: "🇳🇬" },
  { name: "Kenya", code: "KE", flag: "🇰🇪" },
  { name: "Egypt", code: "EG", flag: "🇪🇬" },
  { name: "Morocco", code: "MA", flag: "🇲🇦" },
  { name: "Turkey", code: "TR", flag: "🇹🇷" },
  { name: "Israel", code: "IL", flag: "🇮🇱" },
  { name: "United Arab Emirates", code: "AE", flag: "🇦🇪" },
  { name: "Saudi Arabia", code: "SA", flag: "🇸🇦" },
  { name: "Qatar", code: "QA", flag: "🇶🇦" },
  { name: "Kuwait", code: "KW", flag: "🇰🇼" },
  { name: "Bahrain", code: "BH", flag: "🇧🇭" },
  { name: "Oman", code: "OM", flag: "🇴🇲" },
  { name: "Jordan", code: "JO", flag: "🇯🇴" },
  { name: "Lebanon", code: "LB", flag: "🇱🇧" },
  { name: "Pakistan", code: "PK", flag: "🇵🇰" },
  { name: "Bangladesh", code: "BD", flag: "🇧🇩" },
  { name: "Sri Lanka", code: "LK", flag: "🇱🇰" },
  { name: "Thailand", code: "TH", flag: "🇹🇭" },
  { name: "Vietnam", code: "VN", flag: "🇻🇳" },
  { name: "Malaysia", code: "MY", flag: "🇲🇾" },
  { name: "Indonesia", code: "ID", flag: "🇮🇩" },
  { name: "Philippines", code: "PH", flag: "🇵🇭" },
  { name: "Taiwan", code: "TW", flag: "🇹🇼" },
  { name: "Hong Kong", code: "HK", flag: "🇭🇰" },
  { name: "China", code: "CN", flag: "🇨🇳" },
  { name: "Ukraine", code: "UA", flag: "🇺🇦" },
  { name: "Romania", code: "RO", flag: "🇷🇴" },
  { name: "Bulgaria", code: "BG", flag: "🇧🇬" },
  { name: "Greece", code: "GR", flag: "🇬🇷" },
  { name: "Croatia", code: "HR", flag: "🇭🇷" },
  { name: "Hungary", code: "HU", flag: "🇭🇺" },
  { name: "Czech Republic", code: "CZ", flag: "🇨🇿" },
  { name: "Poland", code: "PL", flag: "🇵🇱" },
  { name: "Lithuania", code: "LT", flag: "🇱🇹" },
  { name: "Latvia", code: "LV", flag: "🇱🇻" },
  { name: "Estonia", code: "EE", flag: "🇪🇪" },
  { name: "Iceland", code: "IS", flag: "🇮🇸" },
  { name: "Malta", code: "MT", flag: "🇲🇹" },
  { name: "Cyprus", code: "CY", flag: "🇨🇾" },
  { name: "Luxembourg", code: "LU", flag: "🇱🇺" },
  { name: "Russia", code: "RU", flag: "🇷🇺" },
  { name: "Venezuela", code: "VE", flag: "🇻🇪" },
  { name: "Ecuador", code: "EC", flag: "🇪🇨" },
  { name: "Bolivia", code: "BO", flag: "🇧🇴" },
  { name: "Paraguay", code: "PY", flag: "🇵🇾" },
  { name: "Uruguay", code: "UY", flag: "🇺🇾" },
  { name: "Other", code: "XX", flag: "🌍" }
];

interface CountrySelectorProps {
  value: string;
  onChange: (country: string) => void;
  onDetected?: (country: string) => void;
  className?: string;
}

export const CountrySelector = ({ 
  value, 
  onChange, 
  onDetected,
  className = "" 
}: CountrySelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter countries based on search term
  const filteredCountries = COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Detect user's country based on IP
  useEffect(() => {
    const detectCountry = async () => {
      if (detectedCountry || isDetecting) return;
      
      setIsDetecting(true);
      try {
        console.log('Detecting country from IP...');
        
        // Try multiple services with better error handling
        let data = null;
        let countryCode = null;
        let countryName = null;
        
        // Service 1: ipapi.co
        try {
          const response = await fetch('https://ipapi.co/json/', {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
          });
          
          if (response.ok) {
            data = await response.json();
            console.log('ipapi.co response:', data);
            if (data.country_code && data.country_name) {
              countryCode = data.country_code;
              countryName = data.country_name;
            }
          }
        } catch (error) {
          console.log('ipapi.co failed:', error);
        }
        
        // Service 2: ipinfo.io (fallback)
        if (!countryCode) {
          try {
            const response = await fetch('https://ipinfo.io/json', {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
              },
            });
            
            if (response.ok) {
              data = await response.json();
              console.log('ipinfo.io response:', data);
              if (data.country) {
                countryCode = data.country;
                countryName = data.country;
              }
            }
          } catch (error) {
            console.log('ipinfo.io failed:', error);
          }
        }
        
        // Service 3: ip-api.com (another fallback)
        if (!countryCode) {
          try {
            const response = await fetch('http://ip-api.com/json', {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
              },
            });
            
            if (response.ok) {
              data = await response.json();
              console.log('ip-api.com response:', data);
              if (data.countryCode && data.country) {
                countryCode = data.countryCode;
                countryName = data.country;
              }
            }
          } catch (error) {
            console.log('ip-api.com failed:', error);
          }
        }
        
        // Find and set the country
        if (countryCode) {
          const country = COUNTRIES.find(c => c.code === countryCode);
          console.log('Found country:', country, 'for code:', countryCode);
          if (country) {
            setDetectedCountry(country.name);
            if (onDetected) {
              onDetected(country.name);
            }
          } else {
            console.log('Country not found in our list for code:', countryCode);
          }
        } else {
          console.log('No country code found from any service');
        }
        
      } catch (error) {
        console.log('All IP detection services failed:', error);
      } finally {
        setIsDetecting(false);
      }
    };

    detectCountry();
  }, [detectedCountry, isDetecting, onDetected]);

  // Calculate dropdown position
  const updateDropdownPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleCloseDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleCloseDropdown();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Update position on scroll and resize
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  const selectedCountry = COUNTRIES.find(country => country.name === value);

  const handleSelectCountry = (countryName: string) => {
    onChange(countryName);
    handleCloseDropdown();
  };

  const handleToggleDropdown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isOpen) {
      handleCloseDropdown();
    } else {
      handleOpenDropdown();
    }
  };

  const handleOpenDropdown = () => {
    updateDropdownPosition();
    setIsOpen(true);
    setIsAnimating(true);
    // Focus search input after animation starts
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 150);
  };

  const handleCloseDropdown = () => {
    setIsAnimating(false);
    setSearchTerm("");
    setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  // Handle country selection with proper event handling
  const handleCountryClick = (countryName: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Ensure the selection works regardless of search state
    console.log('Selecting country:', countryName); // Debug log
    onChange(countryName);
    
    // Close dropdown with slight delay to ensure selection is processed
    setTimeout(() => {
      handleCloseDropdown();
    }, 50);
  };

  // Render dropdown using portal to avoid container clipping
  const renderDropdown = () => {
    if (!isOpen) return null;

    return createPortal(
      <div 
        ref={dropdownRef}
        className={cn(
          "fixed z-[9999] bg-background border border-border rounded-lg shadow-xl",
          "transition-all duration-200 ease-out",
          isAnimating 
            ? "opacity-100 scale-100 translate-y-0" 
            : "opacity-0 scale-95 translate-y-2"
        )}
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input with enhanced styling */}
        <div className="p-3 border-b border-border/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder="Search countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filteredCountries.length > 0) {
                  handleSelectCountry(filteredCountries[0].name);
                }
              }}
            />
          </div>
        </div>

        {/* Country List with smooth scrolling */}
        <div className="max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          {filteredCountries.length > 0 ? (
            <div className="py-1">
              {filteredCountries.map((country, index) => (
                <button
                  key={country.code}
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => handleCountryClick(country.name, e)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm",
                    "hover:bg-accent hover:text-accent-foreground transition-all duration-150",
                    "focus:bg-accent focus:text-accent-foreground focus:outline-none",
                    "active:scale-[0.98] active:bg-accent/80",
                    value === country.name && "bg-primary/10 text-primary font-medium",
                    "animate-in slide-in-from-top-2"
                  )}
                  style={{
                    animationDelay: `${index * 10}ms`,
                    animationFillMode: 'both'
                  }}
                >
                  <FlagEmoji flag={country.flag} countryCode={country.code} className="text-lg flex-shrink-0" />
                  <span className="flex-1 truncate">{country.name}</span>
                  {value === country.name && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-3 py-8 text-center text-muted-foreground text-sm">
              <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No countries found</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className={className} ref={containerRef}>
      <Label htmlFor="country-selector" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
        Country
      </Label>
      
      <div className="relative mt-1">
        {/* Main Trigger Button - Matching onboarding style */}
        <Button
          id="country-selector"
          type="button"
          variant="outline"
          onClick={handleToggleDropdown}
          className={cn(
            "w-full justify-between h-11 px-3 font-normal transition-all duration-200",
            "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
            "focus:ring-2 focus:ring-primary/20 focus:border-primary",
            isOpen && "ring-2 ring-primary/20 border-primary bg-primary/5"
          )}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            {selectedCountry ? (
              <>
                <FlagEmoji flag={selectedCountry.flag} countryCode={selectedCountry.code} className="text-lg flex-shrink-0" />
                <span className="truncate text-left">{selectedCountry.name}</span>
              </>
            ) : (
              <>
                <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">Select your country</span>
              </>
            )}
          </div>
          <ChevronDown 
            className={cn(
              "h-4 w-4 transition-transform duration-300 flex-shrink-0",
              isOpen && "transform rotate-180"
            )} 
          />
        </Button>

        {/* IP Detection Banner - Enhanced styling */}
        {detectedCountry && !value && !isOpen && (
          <div className="mt-2 p-3 bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg animate-in slide-in-from-top-2">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-primary font-medium truncate">We detected you're in {detectedCountry}</p>
                <p className="text-primary/70 text-xs">Click to use this location</p>
              </div>
              <Button
                size="sm"
                onClick={() => handleSelectCountry(detectedCountry)}
                className="h-7 px-3 text-xs flex-shrink-0 transition-all duration-200 hover:scale-105"
              >
                Use this
              </Button>
            </div>
          </div>
        )}
        

      </div>

      {/* Render dropdown using portal */}
      {renderDropdown()}
    </div>
  );
};